// 공사 안내문 QR 의 `site` 값 — 형식 검증과 유입 경로 결정. 저장소 밖에 두어 인메모리·Supabase 가 한 벌을 쓴다.

import { z } from "zod";

import { CUSTOMER_SOURCES, type CustomerSource, type IntakeSource } from "./types";

// 접수 폼 hidden 칸 이름 — 주소창 `?site=` 를 그대로 옮겨 싣는다.
export const SITE_FIELD = "site";

// 현장 id 는 uuid(Supabase)거나 prj_xxx(인메모리) — 그 밖의 글자·길이는 받지 않는다.
export const siteParamSchema = z
  .string()
  .trim()
  .min(1, "현장 값이 비었습니다")
  .max(64, "현장 값이 너무 깁니다")
  .regex(/^[A-Za-z0-9_-]+$/, "현장 값 형식이 맞지 않습니다");

// 폼 hidden·주소창 값을 현장 id 후보로 — 형식이 틀리면 조용히 없는 것으로 본다(이웃에게 오류를 보이지 않는다).
export const parseSiteParam = (raw: unknown): string | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== "string") return null;
  const parsed = siteParamSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export interface IntakeSiteDecision {
  source: Extract<IntakeSource, "intake" | "notice">;
  source_project_id: string | null;
}

// 조회한 현장이 그 조직 것일 때만 NOTICE — 없거나 다른 조직이면 기존 간편접수 그대로.
export const resolveIntakeSite = (
  orgId: string,
  siteId: string | null,
  project: { id: string; org_id: string } | null
): IntakeSiteDecision => {
  // uuid 는 대소문자를 가리지 않는다 — DB 는 소문자로 돌려주므로 주소창의 대문자와 맞춰 본다.
  if (siteId && project && project.id.toLowerCase() === siteId.toLowerCase() && project.org_id === orgId) {
    return { source: "notice", source_project_id: project.id };
  }
  return { source: "intake", source_project_id: null };
};

// 손 등록·편집에서 「공사 안내문」 을 새로 고르면 거절 문구 — 현장 없는 안내문 유입이 생기지 않게.
export const NOTICE_MANUAL_ERROR = "공사 안내문 유입은 안내문 QR 접수로만 붙습니다";

// 손으로 「공개 페이지」 를 새로 고르면 거절 문구 — 공개 페이지의 상담 문의로만 붙는다.
export const PAGE_MANUAL_ERROR = "공개 페이지 유입은 공개 페이지의 상담 문의로만 붙습니다";

// 접수로만 붙는 유입 → 거절 문구. 손 등록·편집이 새로 고를 수 없다.
const INTAKE_ONLY_SOURCES: Partial<Record<CustomerSource, string>> = {
  NOTICE: NOTICE_MANUAL_ERROR,
  PAGE: PAGE_MANUAL_ERROR,
};

// 이미 그 값인 고객을 그대로 두는 편집은 통과 — 비활성 코드(`codesError`)와 같은 규칙.
export const noticeSourceError = (next: unknown, current: CustomerSource | null = null): string | null => {
  const source = CUSTOMER_SOURCES.find((value) => value === next);
  if (!source || source === current) return null;
  return INTAKE_ONLY_SOURCES[source] ?? null;
};

// 손 등록·편집의 유입 선택지 — 공사 안내문·공개 페이지는 지금 값이 그것일 때만 남긴다.
export const manualSourceChoices = (current: CustomerSource | null = null): CustomerSource[] =>
  CUSTOMER_SOURCES.filter((value) => !INTAKE_ONLY_SOURCES[value] || current === value);
